import { action, makeAutoObservable, runInAction } from 'mobx';
import { observable }                              from 'mobx';
import { ApiCollection }                           from 'collections/ApiCollection';
import AdvertiserModel                             from 'models/AdvertiserModel';
import AdvertiserShowModel                         from 'models/AdvertiserShowModel';
import DemandConfigModel                           from 'models/DemandConfigModel';
import ProviderModel                               from 'models/ProviderModel';
import ProviderShowCategoryModel                   from 'models/ProviderShowCategoryModel';
import ProviderShowModel                           from 'models/ProviderShowModel';
import ShowModel                                   from 'models/ShowModel';

export default class PrivateLayoutStore {
  @observable public advertiserCollection = new ApiCollection(AdvertiserModel);
  @observable public advertiserShowCollection = new ApiCollection(AdvertiserShowModel);
  @observable public demandConfigCollection = new ApiCollection(DemandConfigModel);
  @observable public providerCollection = new ApiCollection(ProviderModel);
  @observable public providerShowCategoryCollection = new ApiCollection(ProviderShowCategoryModel);
  @observable public providerShowCollection = new ApiCollection(ProviderShowModel);
  @observable public show = new ShowModel();
  @observable public showCollection = new ApiCollection(ShowModel);
  @observable public showId: id = undefined;

  constructor() {
    makeAutoObservable(this);
  }

  public fetchAdvertisingData = async () => {
    if (this.showId) {
      await Promise.all([
        this.advertiserShowCollection
          .setFilter('show', this.showId)
          .list(),

        this.advertiserCollection
          .setFilter('advertiserShows.show', this.showId)
          .list(),
      ]);
    }
  };

  public fetchData = async () => {
    return Promise.all([
      this.showCollection
        .setFilter('enabled', true)
        .setSort('createdAt', false)
        .list(),

      this.fetchAdvertisingData(),
      this.fetchDemandConfigData(),
      this.fetchProvidingData(),
      // Missing/forbidden show is handled by the views through
      // setCurrentShowId (redirect to /404 or /erreur) - don't let the global
      // layout load end up as an unhandled rejection.
      this.fetchShowData().catch((error: any) => {
        if (error?.name !== 'show_not_found' && error?.name !== 'get_show_unauthorized') {
          throw error;
        }
      }),
    ]);
  };

  public fetchDemandConfigData = async () => {
    if (this.showId) {
      await this.demandConfigCollection
        .setFilter('show', this.showId)
        .list();
    }
  };

  public fetchProvidingData = async () => {
    if (this.showId) {
      await Promise.all([
        this.providerCollection
          .setFilter('providerShows.show', this.showId)
          .list(),

        this.providerShowCategoryCollection
          .list(),

        this.providerShowCollection
          .setFilter('show', this.showId)
          .list(),
      ]);
    }
  };

  public fetchShowData = async () => {
    if (this.showId) {
      this.show.clear();
      this.show.setId(this.showId);

      try {
        await this.show.fetch();
      } catch (error: any) {
        const requestStatus = error?.connectorError?.request?.status || error?.connectorError?.response?.status;

        // Forget the show so a later setCurrentShowId with the same id
        // fetches it again (and redirects again) instead of being a no-op.
        if (requestStatus === 404 || requestStatus === 405) {
          runInAction(() => { this.showId = undefined; });
        }

        if (requestStatus === 404) {
          const error = new Error('show_not_found');
          error.name = 'show_not_found';
          throw error;
        }

        if (requestStatus === 405) {
          const error = new Error('get_show_unauthorized');
          error.name = 'get_show_unauthorized';
          throw error;
        }
      }
    }
  };

  @action
  public setCurrentShowId = async (showId: any) => {
      const parsedShowId = parseInt(showId);
      if (parsedShowId !== this.showId) {
        this.showId = parsedShowId;

        await this.fetchShowData();

        await Promise.all([
          this.fetchAdvertisingData(),
          this.fetchDemandConfigData(),
          this.fetchProvidingData(),
        ]);
      }
    };
}
